import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FaCopy, FaSyncAlt, FaTimes } from 'react-icons/fa'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Notify } from 'notiflix/build/notiflix-notify-aio'
import api from '../../../helpers/apiRequester'
import { CollapsibleSection } from '../omnibusShared/collapsibleSection'
import styles from './styles.module.scss'
import { ACCESS_LOG_BASE, Pill, formatDateTime, handleAdminApiError } from './shared'

export interface AccessKey {
    id: number
    key_value: string
    label: string
    holder_name: string | null
    holder_email: string | null
    organisation: string | null
    notes: string | null
    /**
     * 'dashboard' = built-in key behind every bundle link in this dashboard (always on);
     * 'master'    = the legacy shared raw key (switchable, no expiry);
     * null        = an issued alias key.
     */
    system_role: 'dashboard' | 'master' | null
    enabled: boolean
    expired: boolean
    expires_at: string | null
    created_at: string
    created_by: string | null
    disabled_at: string | null
    disabled_by: string | null
    last_used_at: string | null
    use_count: number
    total_accesses: number
    successful_downloads: number
    unique_ips: number
    unique_batches: number
}

interface KeyForm {
    label: string
    holderName: string
    holderEmail: string
    organisation: string
    notes: string
    expiresAt: string
}

const EMPTY_FORM: KeyForm = { label: '', holderName: '', holderEmail: '', organisation: '', notes: '', expiresAt: '' }

const toDateInput = (raw: string | null) => (raw ? new Date(raw).toISOString().slice(0, 10) : '')

const toForm = (key: AccessKey): KeyForm => ({
    label: key.label,
    holderName: key.holder_name || '',
    holderEmail: key.holder_email || '',
    organisation: key.organisation || '',
    notes: key.notes || '',
    expiresAt: toDateInput(key.expires_at),
})

const copyToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text)
        Notify.success('Copied to clipboard')
    } catch {
        Notify.failure('Could not copy — select the key and copy manually')
    }
}

const exampleUrl = (keyValue: string) => `https://snt.legal/<short-code>?raw=1&rawkey=${keyValue}`

const maskKey = (value: string) => `${value.slice(0, 4)}${'•'.repeat(Math.max(4, value.length - 8))}${value.slice(-4)}`

const isDashboardKey = (k: Pick<AccessKey, 'system_role'>) => k.system_role === 'dashboard'
const isMasterKey = (k: Pick<AccessKey, 'system_role'>) => k.system_role === 'master'
const isSystemKey = (k: Pick<AccessKey, 'system_role'>) => k.system_role !== null

const KeyTypePill = ({ k }: { k: Pick<AccessKey, 'system_role'> }) =>
    isDashboardKey(k) ? (
        <Pill tone="blue">Dashboard</Pill>
    ) : isMasterKey(k) ? (
        <Pill tone="warn">Master</Pill>
    ) : (
        <Pill tone="muted">Alias</Pill>
    )

const KeyStatus = ({ k }: { k: Pick<AccessKey, 'enabled' | 'expired' | 'system_role'> }) =>
    isDashboardKey(k) ? (
        <Pill tone="ok">Always enabled</Pill>
    ) : k.expired ? (
        <Pill tone="warn">Expired</Pill>
    ) : k.enabled ? (
        <Pill tone="ok">Enabled</Pill>
    ) : (
        <Pill tone="bad">Disabled</Pill>
    )

const Field = ({
    label,
    value,
    mono,
    wide,
}: {
    label: string
    value: React.ReactNode
    mono?: boolean
    wide?: boolean
}) => (
    <div className={`${styles.detailField} ${wide ? styles.detailWide : ''}`}>
        <dt>{label}</dt>
        <dd className={mono ? styles.mono : undefined}>
            {value === null || value === undefined || value === '' ? <span className={styles.muted}>—</span> : value}
        </dd>
    </div>
)

/**
 * Admin management of raw-download keys. The old shared master key is retired;
 * raw downloads require one of these named, switchable keys, and every
 * access-log row records which key authorised it.
 */
export const DocumentBundleAccessKeysView = () => {
    const navigate = useNavigate()
    const [keys, setKeys] = useState<AccessKey[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    // CollapsibleSection owns its open state; bumping this key remounts it so
    // the create form folds away again after a key has been issued.
    const [createSectionKey, setCreateSectionKey] = useState(0)
    const [form, setForm] = useState<KeyForm>(EMPTY_FORM)
    const [saving, setSaving] = useState(false)
    const [busyId, setBusyId] = useState<number | null>(null)
    const [revealed, setRevealed] = useState<Record<number, boolean>>({})
    const [justCreated, setJustCreated] = useState<{ id: number; keyValue: string; label: string } | null>(null)
    // Modal: which key is open, and whether it is showing the edit form.
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [editing, setEditing] = useState(false)
    const [editForm, setEditForm] = useState<KeyForm>(EMPTY_FORM)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const { data } = await api({ url: '/document-access-keys', data: {} })
            if (!data?.success) throw new Error(data?.message || 'Failed to load keys')
            setKeys(data.keys || [])
        } catch (err: any) {
            if (handleAdminApiError(err, navigate)) return
            setError(err?.response?.data?.message || err?.message || 'Failed to load keys')
        } finally {
            setLoading(false)
        }
    }, [navigate])

    useEffect(() => {
        load()
    }, [load])

    // Derived from the list so the modal reflects the reload after any change.
    const selected = useMemo(() => keys.find((k) => k.id === selectedId) ?? null, [keys, selectedId])

    const openKey = (key: AccessKey) => {
        setSelectedId(key.id)
        setEditing(false)
    }

    const closeModal = useCallback(() => {
        if (busyId !== null) return
        setSelectedId(null)
        setEditing(false)
    }, [busyId])

    useEffect(() => {
        if (selectedId === null) return undefined
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeModal()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [selectedId, closeModal])

    const onCreate = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.label.trim()) {
            Notify.failure('A label is required')
            return
        }
        setSaving(true)
        try {
            const { data } = await api({
                url: '/document-access-keys-create',
                data: {
                    label: form.label,
                    holderName: form.holderName || undefined,
                    holderEmail: form.holderEmail || undefined,
                    organisation: form.organisation || undefined,
                    notes: form.notes || undefined,
                    expiresAt: form.expiresAt || undefined,
                },
            })
            if (!data?.success) throw new Error(data?.message || 'Failed to create key')
            setJustCreated({ id: data.id, keyValue: data.keyValue, label: form.label })
            setRevealed((r) => ({ ...r, [data.id]: true }))
            setForm(EMPTY_FORM)
            setCreateSectionKey((k) => k + 1)
            Notify.success('Access key created')
            await load()
        } catch (err: any) {
            if (handleAdminApiError(err, navigate)) return
            Notify.failure(err?.response?.data?.message || err?.message || 'Failed to create key')
        } finally {
            setSaving(false)
        }
    }

    const patchKey = async (id: number, patch: Record<string, unknown>, successMessage: string) => {
        setBusyId(id)
        try {
            const { data } = await api({ url: '/document-access-keys-update', data: { id, ...patch } })
            if (!data?.success) throw new Error(data?.message || 'Update failed')
            Notify.success(successMessage)
            await load()
            return true
        } catch (err: any) {
            if (handleAdminApiError(err, navigate)) return false
            Notify.failure(err?.response?.data?.message || err?.message || 'Update failed')
            return false
        } finally {
            setBusyId(null)
        }
    }

    const toggleEnabled = (key: AccessKey) => {
        if (key.enabled) {
            Confirm.show(
                'Disable access key',
                `Disable "${key.label}"? Any raw download links using this key will stop working immediately. You can re-enable it later.`,
                'Disable',
                'Cancel',
                () => patchKey(key.id, { enabled: false }, `"${key.label}" disabled`),
                undefined,
                { okButtonBackground: '#b91c1c' }
            )
        } else if (isMasterKey(key)) {
            Confirm.show(
                'Enable the shared master key',
                'This is the legacy key that was sent out with bundle links. Enabling it lets anyone who still has it download raw PDFs for every bundle, with no way to tell them apart in the log. Prefer issuing a named alias key. Enable anyway?',
                'Enable master key',
                'Cancel',
                () => patchKey(key.id, { enabled: true }, `"${key.label}" enabled`),
                undefined,
                { okButtonBackground: '#b45309' }
            )
        } else {
            patchKey(key.id, { enabled: true }, `"${key.label}" enabled`)
        }
    }

    const startEdit = (key: AccessKey) => {
        setEditForm(toForm(key))
        setEditing(true)
    }

    const saveEdit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (selectedId === null || !selected) return
        const ok = await patchKey(
            selectedId,
            {
                label: editForm.label,
                holderName: editForm.holderName,
                holderEmail: editForm.holderEmail,
                organisation: editForm.organisation,
                notes: editForm.notes,
                // System keys never expire; don't send the field at all.
                ...(isSystemKey(selected) ? {} : { expiresAt: editForm.expiresAt || null }),
            },
            'Key details saved'
        )
        if (ok) setEditing(false)
    }

    const renderForm = (
        values: KeyForm,
        setValues: (v: KeyForm) => void,
        onSubmit: (e: React.FormEvent) => void,
        onCancel: () => void,
        submitLabel: string,
        cancelLabel = 'Cancel',
        options: { allowExpiry?: boolean } = {}
    ) => (
        <form className={styles.keyForm} onSubmit={onSubmit}>
            <div className={styles.formGrid}>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>Label *</span>
                    <input
                        className={styles.formInput}
                        type="text"
                        required
                        placeholder="e.g. Funder X – Jane"
                        value={values.label}
                        onChange={(e) => setValues({ ...values, label: e.target.value })}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>Holder name</span>
                    <input
                        className={styles.formInput}
                        type="text"
                        value={values.holderName}
                        onChange={(e) => setValues({ ...values, holderName: e.target.value })}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>Holder email</span>
                    <input
                        className={styles.formInput}
                        type="email"
                        value={values.holderEmail}
                        onChange={(e) => setValues({ ...values, holderEmail: e.target.value })}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>Organisation</span>
                    <input
                        className={styles.formInput}
                        type="text"
                        value={values.organisation}
                        onChange={(e) => setValues({ ...values, organisation: e.target.value })}
                    />
                </label>
                {options.allowExpiry !== false && (
                    <label className={styles.formField}>
                        <span className={styles.formLabel}>Expires (optional)</span>
                        <input
                            className={styles.formInput}
                            type="date"
                            value={values.expiresAt}
                            onChange={(e) => setValues({ ...values, expiresAt: e.target.value })}
                        />
                    </label>
                )}
                <label className={`${styles.formField} ${styles.formFieldWide}`}>
                    <span className={styles.formLabel}>Notes</span>
                    <input
                        className={styles.formInput}
                        type="text"
                        placeholder="Who this key was issued to and why (optional)"
                        value={values.notes}
                        onChange={(e) => setValues({ ...values, notes: e.target.value })}
                    />
                </label>
            </div>
            <div className={styles.formActions}>
                <button type="button" className={styles.secondaryBtn} onClick={onCancel}>
                    {cancelLabel}
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={saving || busyId !== null}>
                    {submitLabel}
                </button>
            </div>
        </form>
    )

    // Stats describe issued alias keys; the built-in dashboard key is listed
    // but not counted as one of them.
    const aliasKeys = useMemo(() => keys.filter((k) => !isSystemKey(k)), [keys])
    const masterKey = useMemo(() => keys.find(isMasterKey) ?? null, [keys])
    const enabledCount = aliasKeys.filter((k) => k.enabled && !k.expired).length

    const renderModal = (k: AccessKey) => {
        const isBusy = busyId === k.id
        const shown = revealed[k.id]
        const system = isSystemKey(k)
        const dashboard = isDashboardKey(k)
        const master = isMasterKey(k)
        return (
            <div className={styles.modalOverlay} onClick={closeModal}>
                <div
                    className={styles.modal}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="access-key-modal-title"
                >
                    <div className={styles.modalHeader}>
                        <div>
                            <h3 id="access-key-modal-title" className={styles.modalTitle}>
                                {k.label}
                            </h3>
                            <p className={styles.modalSubtitle}>
                                {dashboard
                                    ? 'Built-in key used by every bundle link in this dashboard. Cannot be disabled, expired or deleted.'
                                    : master
                                      ? 'The legacy shared raw-download key sent out with bundle links. Kept switched off; can be enabled here if a recipient must be unblocked.'
                                      : [k.organisation, k.holder_name].filter(Boolean).join(' · ') || 'No holder recorded'}
                            </p>
                        </div>
                        <button
                            type="button"
                            className={styles.modalClose}
                            onClick={closeModal}
                            disabled={isBusy}
                            aria-label="Close key details"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        {editing ? (
                            <div className={styles.modalSection}>
                                <h4 className={styles.modalSectionTitle}>Edit key</h4>
                                {renderForm(editForm, setEditForm, saveEdit, () => setEditing(false), 'Save changes', 'Cancel', {
                                    allowExpiry: !system,
                                })}
                            </div>
                        ) : (
                            <>
                                <div className={styles.modalSection}>
                                    <h4 className={styles.modalSectionTitle}>Usage</h4>
                                    <div className={styles.statStrip}>
                                        <div className={styles.stat}>
                                            <span className={styles.statValue}>
                                                {k.successful_downloads.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.statLabel}>Downloads</span>
                                        </div>
                                        <div className={styles.stat}>
                                            <span className={styles.statValue}>
                                                {k.total_accesses.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.statLabel}>Requests</span>
                                        </div>
                                        <div className={styles.stat}>
                                            <span className={styles.statValue}>{k.unique_ips.toLocaleString('en-GB')}</span>
                                            <span className={styles.statLabel}>Unique IPs</span>
                                        </div>
                                        <div className={styles.stat}>
                                            <span className={styles.statValue}>
                                                {k.unique_batches.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.statLabel}>Batches</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.modalSection}>
                                    <h4 className={styles.modalSectionTitle}>Key</h4>
                                    <div className={styles.keyReveal}>
                                        <div className={styles.keyRevealRow}>
                                            <span className={styles.refCode}>{shown ? k.key_value : maskKey(k.key_value)}</span>
                                            <button
                                                type="button"
                                                className={`${styles.secondaryBtn} ${styles.smallBtn}`}
                                                onClick={() => setRevealed((r) => ({ ...r, [k.id]: !shown }))}
                                            >
                                                {shown ? 'Hide' : 'Show'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.secondaryBtn} ${styles.smallBtn}`}
                                                onClick={() => copyToClipboard(k.key_value)}
                                            >
                                                <FaCopy /> Copy key
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.secondaryBtn} ${styles.smallBtn}`}
                                                onClick={() => copyToClipboard(`?raw=1&rawkey=${k.key_value}`)}
                                            >
                                                <FaCopy /> Copy query string
                                            </button>
                                        </div>
                                        {dashboard ? (
                                            <p className={styles.note}>
                                                The API appends this key to every bundle link it returns to the dashboard
                                                (viewer and raw download), so those opens are attributed here. You should
                                                not need to share it.
                                            </p>
                                        ) : (
                                            <p className={styles.note}>
                                                Example:{' '}
                                                <span className={styles.mono}>{exampleUrl(shown ? k.key_value : '<key>')}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.modalSection}>
                                    <h4 className={styles.modalSectionTitle}>Details</h4>
                                    <dl className={styles.detailGrid}>
                                        <Field label="Status" value={<KeyStatus k={k} />} />
                                        <Field label="Type" value={<KeyTypePill k={k} />} />
                                        <Field label="Holder" value={k.holder_name} />
                                        <Field label="Holder email" value={k.holder_email} />
                                        <Field label="Organisation" value={k.organisation} />
                                        <Field label="Last used" value={k.last_used_at ? formatDateTime(k.last_used_at) : 'Never'} />
                                        <Field
                                            label="Expires"
                                            value={system ? 'Never' : k.expires_at ? formatDateTime(k.expires_at) : 'No expiry'}
                                        />
                                        <Field
                                            label="Created"
                                            value={
                                                <>
                                                    {formatDateTime(k.created_at)}
                                                    {k.created_by && <span className={styles.muted}> by {k.created_by}</span>}
                                                </>
                                            }
                                        />
                                        {(k.disabled_at || k.disabled_by) && (
                                            <Field
                                                label="Disabled"
                                                value={
                                                    <>
                                                        {formatDateTime(k.disabled_at)}
                                                        {k.disabled_by && (
                                                            <span className={styles.muted}> by {k.disabled_by}</span>
                                                        )}
                                                    </>
                                                }
                                            />
                                        )}
                                        <Field label="Notes" value={k.notes} wide />
                                    </dl>
                                </div>
                            </>
                        )}
                    </div>

                    {!editing && (
                        <div className={styles.modalFooter}>
                            <div className={styles.modalFooterGroup}>
                                {!dashboard && (
                                    <button
                                        type="button"
                                        className={k.enabled ? styles.dangerBtn : styles.secondaryBtn}
                                        disabled={isBusy}
                                        onClick={() => toggleEnabled(k)}
                                    >
                                        {k.enabled ? 'Disable key' : 'Enable key'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    disabled={isBusy}
                                    onClick={() => startEdit(k)}
                                >
                                    Edit details
                                </button>
                            </div>
                            <Link
                                className={styles.secondaryBtn}
                                to={`${ACCESS_LOG_BASE}?accessKeyId=${k.id}`}
                                title="Open the access log filtered to this key"
                            >
                                View downloads
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.introRow}>
                <p className={styles.intro}>
                    Every key that can authorise a raw bundle download (<code>?raw=1&amp;rawkey=…</code>) is listed
                    here with its current status. Give each person or funder their own alias key; switching a key off
                    blocks their links immediately while every download they made stays attributed to them in the{' '}
                    <Link to={ACCESS_LOG_BASE}>access log</Link>. The legacy <Pill tone="warn">Master</Pill> key is
                    kept switched off and can be re-enabled here if needed. The built-in{' '}
                    <Pill tone="blue">Dashboard</Pill> key is used automatically by every bundle link in this dashboard
                    and cannot be disabled.
                </p>
                <button type="button" className={styles.secondaryBtn} onClick={() => load()} disabled={loading}>
                    <FaSyncAlt /> Refresh
                </button>
            </div>

            <div className={styles.statStrip}>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{aliasKeys.length.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Alias keys</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{enabledCount.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Enabled</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{(aliasKeys.length - enabledCount).toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Disabled or expired</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>
                        {aliasKeys.reduce((n, k) => n + k.successful_downloads, 0).toLocaleString('en-GB')}
                    </span>
                    <span className={styles.statLabel}>Downloads via aliases</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>
                        {keys
                            .filter(isDashboardKey)
                            .reduce((n, k) => n + k.total_accesses, 0)
                            .toLocaleString('en-GB')}
                    </span>
                    <span className={styles.statLabel}>Opened from dashboard</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>
                        {masterKey ? (
                            <KeyStatus k={masterKey} />
                        ) : (
                            <Pill tone="muted">Not seeded</Pill>
                        )}
                    </span>
                    <span className={styles.statLabel}>Master key</span>
                </div>
            </div>

            <CollapsibleSection
                key={createSectionKey}
                title="New access key"
                hint="Only the label is required. The key value is generated for you and shown once the key is created."
                defaultOpen={false}
            >
                {renderForm(
                    form,
                    setForm,
                    onCreate,
                    () => setForm(EMPTY_FORM),
                    saving ? 'Creating…' : 'Create key',
                    'Clear'
                )}
            </CollapsibleSection>

            {justCreated && (
                <CollapsibleSection
                    title="Key created"
                    count={justCreated.label}
                    actions={
                        <button
                            type="button"
                            className={`${styles.secondaryBtn} ${styles.smallBtn}`}
                            onClick={() => setJustCreated(null)}
                        >
                            Dismiss
                        </button>
                    }
                >
                    <div className={styles.keyReveal}>
                        <div className={styles.keyRevealRow}>
                            <span className={styles.formLabel}>Key</span>
                            <span className={styles.refCode}>{justCreated.keyValue}</span>
                            <button
                                type="button"
                                className={`${styles.secondaryBtn} ${styles.smallBtn}`}
                                onClick={() => copyToClipboard(justCreated.keyValue)}
                            >
                                <FaCopy /> Copy key
                            </button>
                        </div>
                        <div className={styles.keyRevealRow}>
                            <span className={styles.formLabel}>Example link</span>
                            <span className={styles.mono}>{exampleUrl(justCreated.keyValue)}</span>
                            <button
                                type="button"
                                className={`${styles.secondaryBtn} ${styles.smallBtn}`}
                                onClick={() => copyToClipboard(`?raw=1&rawkey=${justCreated.keyValue}`)}
                            >
                                <FaCopy /> Copy query string
                            </button>
                        </div>
                        <p className={styles.note}>
                            Append <span className={styles.mono}>?raw=1&amp;rawkey=&lt;key&gt;</span> to any bundle short
                            link to download the raw PDF. The key can be viewed again from the table below.
                        </p>
                    </div>
                </CollapsibleSection>
            )}

            {error && (
                <div className={styles.errorBanner}>
                    {error}{' '}
                    <button type="button" className={styles.linkBtn} onClick={load}>
                        Retry
                    </button>
                </div>
            )}

            <p className={styles.resultLine}>
                {loading
                    ? 'Loading…'
                    : `${keys.length.toLocaleString('en-GB')} keys (${aliasKeys.length.toLocaleString('en-GB')} alias, ${(
                          keys.length - aliasKeys.length
                      ).toLocaleString('en-GB')} system) · click a row for details`}
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Holder</th>
                            <th>Usage</th>
                            <th>Key value</th>
                            <th>Status</th>
                            <th>Last used</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    <div className={styles.emptyState}>
                                        {loading
                                            ? 'Loading access keys…'
                                                            : 'No keys yet. Run the access-key migrations to seed the system keys, and use "New access key" above to issue alias keys to funders or colleagues.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            keys.map((k) => {
                                const shown = revealed[k.id]
                                return (
                                    <tr
                                        key={k.id}
                                        className={`${styles.clickable} ${selectedId === k.id ? styles.current : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => openKey(k)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                openKey(k)
                                            }
                                        }}
                                    >
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary}>
                                                    {k.label}
                                                    {isSystemKey(k) && (
                                                        <>
                                                            {' '}
                                                            <KeyTypePill k={k} />
                                                        </>
                                                    )}
                                                </span>
                                                <span className={styles.stackSecondary}>
                                                    {k.organisation || <span className={styles.stackMeta}>No organisation</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary}>
                                                    {k.holder_name || <span className={styles.muted}>—</span>}
                                                </span>
                                                <span className={styles.stackSecondary}>
                                                    {k.holder_email || <span className={styles.stackMeta}>No email</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary}>
                                                    {k.successful_downloads.toLocaleString('en-GB')} downloads
                                                </span>
                                                <span className={styles.stackSecondary}>
                                                    {k.total_accesses.toLocaleString('en-GB')} requests
                                                </span>
                                            </div>
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className={styles.stackCell}>
                                                <span className={styles.refCode}>{shown ? k.key_value : maskKey(k.key_value)}</span>
                                                <span className={styles.stackSecondary}>
                                                    <button
                                                        type="button"
                                                        className={styles.linkBtn}
                                                        onClick={() => setRevealed((r) => ({ ...r, [k.id]: !shown }))}
                                                    >
                                                        {shown ? 'Hide' : 'Show'}
                                                    </button>
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <KeyStatus k={k} />
                                        </td>
                                        <td>
                                            <span className={k.last_used_at ? undefined : styles.muted}>
                                                {k.last_used_at ? formatDateTime(k.last_used_at) : 'Never'}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {selected && renderModal(selected)}
        </div>
    )
}

export default DocumentBundleAccessKeysView
